import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Card, Input, Button, Space, Select, Typography, Tag, ColorPicker, Modal, Spin, App } from 'antd';
import { Save, Eye, Edit, ArrowLeft, Wand2, MessageSquare } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CodeBlock from './CodeBlock';
// 使用统一的代码高亮组件
import { Document } from '../types';
import { AppSettings } from '../utils/SettingsManager';
import api from '../services/api';
import cacheManager from '../services/cacheManager';

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

interface EditorProps {
  docData: Document;
  onSave: (doc: Document) => void;
  settings: AppSettings;
  editorSource?: 'new' | 'preview' | 'direct';
  onShowVipModal?: () => void;
}

const EditorNew: React.FC<EditorProps> = ({
  docData,
  onSave,
  settings,
  editorSource = 'direct',
  onShowVipModal
}) => {
  const { message } = App.useApp();
  const [title, setTitle] = useState(docData?.title || '');
  const [content, setContent] = useState(docData?.content || '');
  const [tagId, setTagId] = useState<number | null>(docData?.tagId || null);
  const [tagName, setTagName] = useState(docData?.tag?.name || '');
  const [availableTags, setAvailableTags] = useState<any[]>([]);
  const [inputTag, setInputTag] = useState('');
  const [newTagColor, setNewTagColor] = useState('#1890ff');
  const [addingTag, setAddingTag] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // 一键排版相关状态
  const [isFormatting, setIsFormatting] = useState(false);
  const [showFormatConfirm, setShowFormatConfirm] = useState(false);
  const [originalContent, setOriginalContent] = useState('');
  const [formattedContent, setFormattedContent] = useState('');

  // 问问小酷相关状态
  const [aiChatVisible, setAiChatVisible] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [chatMessages, setChatMessages] = useState<Array<{ id: string, type: 'user' | 'ai', content: string, timestamp: Date }>>([]);
  const [currentInput, setCurrentInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [currentAiResponse, setCurrentAiResponse] = useState('');
  const typingQueuesRef = useRef<Record<string, { content: string; queue: string[]; timer: number | null }>>({});
  const chatContainerRef = useRef<HTMLDivElement | null>(null);

  const chatMarkdownComponents = useMemo(() => ({
    code: ({ inline, className, children, ...rest }: any) => (
      <CodeBlock inline={inline} className={className} settings={settings} {...rest}>
        {children}
      </CodeBlock>
    ),
    a: ({ href, children }: any) => (
      <a href={href} target="_blank" rel="noreferrer" style={{ color: settings.darkMode ? '#91caff' : '#1677ff' }}>
        {children}
      </a>
    ),
    p: ({ children }: any) => <p style={{ margin: '0 0 8px 0', wordBreak: 'break-word' }}>{children}</p>,
    ul: ({ children }: any) => <ul style={{ margin: '0 0 8px 18px', padding: 0 }}>{children}</ul>,
    ol: ({ children }: any) => <ol style={{ margin: '0 0 8px 18px', padding: 0 }}>{children}</ol>,
    blockquote: ({ children }: any) => (
      <blockquote style={{ borderLeft: '3px solid #d9d9d9', margin: '8px 0', paddingLeft: '12px', color: '#666' }}>
        {children}
      </blockquote>
    )
  }), [settings]);

  const scrollChatToBottom = useCallback(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, []);

  const clearTypewriterState = (messageId?: string) => {
    if (messageId) {
      const state = typingQueuesRef.current[messageId];
      if (state?.timer) {
        clearInterval(state.timer);
      }
      delete typingQueuesRef.current[messageId];
    } else {
      Object.keys(typingQueuesRef.current).forEach(id => {
        const state = typingQueuesRef.current[id];
        if (state?.timer) {
          clearInterval(state.timer);
        }
        delete typingQueuesRef.current[id];
      });
    }
  };

  const updateAiMessageContent = (messageId: string, content: string) => {
    setChatMessages(prev => prev.map(msg => (msg.id === messageId ? { ...msg, content } : msg)));
  };

  const startTypewriter = (messageId: string) => {
    const state = typingQueuesRef.current[messageId];
    if (!state || state.timer) return;

    state.timer = window.setInterval(() => {
      const currentState = typingQueuesRef.current[messageId];
      if (!currentState) {
        return;
      }

      if (currentState.queue.length === 0) {
        if (currentState.timer) {
          clearInterval(currentState.timer);
          currentState.timer = null;
        }
        return;
      }

      const nextChar = currentState.queue.shift();
      if (nextChar !== undefined) {
        currentState.content += nextChar;
        updateAiMessageContent(messageId, currentState.content);
        setCurrentAiResponse(currentState.content);
      }
    }, 18);
  };

  const enqueueTypewriter = (messageId: string, text: string) => {
    if (!typingQueuesRef.current[messageId]) {
      typingQueuesRef.current[messageId] = { content: '', queue: [], timer: null };
    }
    const state = typingQueuesRef.current[messageId];
    state.queue.push(...Array.from(text));
    startTypewriter(messageId);
  };

  const finalizeTypewriter = (messageId: string, finalText: string) => {
    const state = typingQueuesRef.current[messageId];
    if (!state) return;
    if (state.queue.length > 0) {
      setCurrentAiResponse(finalText);
      return;
    }
    state.content = finalText;
    if (state.timer) {
      clearInterval(state.timer);
      state.timer = null;
    }
    updateAiMessageContent(messageId, finalText);
    setCurrentAiResponse(finalText);
  };

  const resetAiChatState = () => {
    setAiChatVisible(false);
    setSelectedText('');
    setChatMessages([]);
    setCurrentInput('');
    setCurrentAiResponse('');
    setAiLoading(false);
    clearTypewriterState();
  };

  useEffect(() => {
    scrollChatToBottom();
  }, [chatMessages, aiLoading, currentAiResponse, scrollChatToBottom]);

  useEffect(() => () => {
    clearTypewriterState();
  }, []);

  useEffect(() => {
    if (docData) {
      setTitle(docData.title);
      setContent(docData.content);
      setTagId(docData.tagId || null);
      setTagName(docData.tag?.name || '');
    }
  }, [docData]);

  // 加载标签
  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      const response = await api.get('/tags');
      setAvailableTags(response.data || []);
    } catch (error) {
      // 静默处理加载标签失败
    }
  };

  useEffect(() => {
    const hasChanges =
      title !== (docData?.title || '') ||
      content !== (docData?.content || '') ||
      tagId !== (docData?.tagId || null);
    setHasUnsavedChanges(hasChanges);
  }, [title, content, tagId, docData]);

  // 处理标签选择
  const handleSelectTag = (value: number) => {
    const selectedTag = availableTags.find(tag => tag.id === value);
    if (selectedTag) {
      setTagId(value);
      setTagName(selectedTag.name);
      setHasUnsavedChanges(true);
    }
  };

  // 移除标签
  const handleRemoveTag = () => {
    setTagId(null);
    setTagName('');
    setHasUnsavedChanges(true);
  };

  // 添加新标签
  const handleAddTag = async () => {
    if (!inputTag.trim()) {
      message.error('请输入标签名称');
      return;
    }

    setAddingTag(true);
    try {
      const response = await api.post('/tags', {
        name: inputTag.trim(),
        color: newTagColor
      });

      const newTag = response.data;
      setAvailableTags(prev => [...prev, newTag]);
      setTagId(newTag.id);
      setTagName(newTag.name);
      setInputTag('');
      setNewTagColor('#1890ff');
      setHasUnsavedChanges(true);
      message.success('标签添加成功');
    } catch (error) {
      message.error('添加标签失败');
    } finally {
      setAddingTag(false);
    }
  };

  const handleSave = () => {
    if (!title.trim()) {
      message.error('请输入文档标题');
      return;
    }

    const updatedDoc: Document = {
      ...docData,
      title: title.trim(),
      content,
      // 保证传递给 Document 的 tagId 为 number | undefined，而不是 null
      tagId: tagId ?? undefined,
      tag: tagId ? {
        id: tagId,
        name: tagName,
        color: availableTags.find(t => t.id === tagId)?.color || '#1890ff',
        userId: null,
        createdAt: null,
        updatedAt: null
      } : undefined,
      updatedAt: new Date(),
    };

    onSave(updatedDoc);
    setHasUnsavedChanges(false);
    message.success('保存成功');
  };

  const togglePreview = () => {
    setIsPreview(!isPreview);
  };

  // 一键排版功能（非流式）
  const handleOneClickFormat = async () => {
    if (!content.trim()) {
      message.warning('请先输入内容再进行排版');
      return;
    }

    try {
      // 检查排版使用次数
      const formatUsageResponse = await api.get('/format/usage');
      const formatUsage = formatUsageResponse.data;

      if (!formatUsage.isVip && formatUsage.remaining <= 0) {
        Modal.confirm({
          title: '一键排版次数已用完',
          content: `您今日已使用 ${formatUsage.todayUsed}/${formatUsage.dailyLimit} 次。升级VIP会员可享受无限次数排版。`,
          okText: '升级VIP',
          cancelText: '我知道了',
          onOk: () => {
            if (onShowVipModal) {
              onShowVipModal();
            }
          }
        });
        return;
      }

      setIsFormatting(true);
      setOriginalContent(content);
      setFormattedContent('');
      setShowFormatConfirm(true);

      // 使用非流式API进行排版
      const response = await api.post('/format/sync', { content });

      if (response.data.success) {
        const formattedResult = response.data.content;
        setFormattedContent(formattedResult);
        setIsFormatting(false);
        // 更新缓存
        cacheManager.updateCache('formatUsage').catch(err => console.warn('更新格式化使用情况缓存失败:', err));
        message.success('排版完成');
      } else {
        throw new Error('排版失败');
      }

    } catch (error: any) {
      if (error.response?.status === 429 || error.response?.data?.message?.includes('次数')) {
        message.error(error.response?.data?.message || '您今日的一键排版次数已用完');
      } else if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('排版失败，请重试');
      }

      setIsFormatting(false);
      setShowFormatConfirm(false);
    }
  };

  // 应用排版结果
  const applyFormatting = () => {
    setContent(formattedContent);
    setShowFormatConfirm(false);
    setHasUnsavedChanges(true);
    message.success('排版已应用');
  };

  // 问问小酷功能
  const handleAskAI = async () => {
    // 如果有选中文字，自动填入输入框
    if (selectedText.trim()) {
      setCurrentInput(selectedText);
    }
    setAiChatVisible(true);
  };

  // 发送消息到AI
  const sendMessageToAI = async (messageContent: string) => {
    if (!messageContent.trim()) return;

    // 添加用户消息
    const userMessage = {
      id: Date.now().toString(),
      type: 'user' as const,
      content: messageContent,
      timestamp: new Date()
    };
    setChatMessages(prev => [...prev, userMessage]);
    setCurrentInput('');
    setAiLoading(true);
    setCurrentAiResponse('');
    let aiMessageId: string | null = null;
    let streamedContent = '';

    try {
      // 创建新会话
      const sessionResponse = await api.post('/ai-chat/sessions', {
        title: `关于"${messageContent.substring(0, 20)}..."`
      });
      const sessionId = sessionResponse.data.id;

      // 添加AI消息占位符
      aiMessageId = (Date.now() + 1).toString();
      const aiMessage = {
        id: aiMessageId,
        type: 'ai' as const,
        content: '',
        timestamp: new Date()
      };
      setChatMessages(prev => [...prev, aiMessage]);
      typingQueuesRef.current[aiMessageId] = { content: '', queue: [], timer: null };

      // 使用GET请求和URL参数（与桌面版一致）
      // 开发环境走 '/api' 以使用 devServer 代理，生产保持注入的 baseURL
      const isDevEnv = typeof process !== 'undefined' && process.env?.NODE_ENV === 'development';
      const isLocalHost = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1|0\.0\.0\.0|192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(window.location.hostname);
      const apiBaseUrl = (isDevEnv || isLocalHost) ? '/api' : (api.defaults.baseURL || 'https://note.name666.top/api');
      const normalizedBase = (apiBaseUrl || '').replace(/\/$/, '');
      const path = `/ai-chat/sessions/${sessionId}/stream-messages`;
      const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
      const buildUrl = (base: string, targetPath: string) => {
        const cleanedPath = targetPath.replace(/^\//, '');
        if (!base) {
          return new URL(targetPath, origin);
        }
        if (/^https?:\/\//.test(base)) {
          const normalized = base.endsWith('/') ? base : `${base}/`;
          return new URL(cleanedPath, normalized);
        }
        const prefix = base.endsWith('/') ? base : `${base}/`;
        return new URL(`${prefix}${cleanedPath}`, origin);
      };

      const url = buildUrl(normalizedBase, path);
      url.searchParams.append('content', messageContent);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        // 尝试解析后端返回的错误信息
        let backendMsg = '';
        try {
          const text = await response.text();
          // 可能是 JSON 或纯文本
          if (text.startsWith('{')) {
            const json = JSON.parse(text);
            backendMsg = json.message || json.error || '';
          } else {
            backendMsg = text;
          }
        } catch { /* ignore */ }
        const status = response.status;
        if (status === 401) {
          throw new Error('未登录或登录已过期，请重新登录后再试');
        }
        if (status === 403) {
          throw new Error(backendMsg || '权限不足或已超出使用次数');
        }
        throw new Error(backendMsg || `HTTP错误(${status})，请稍后重试`);
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      if (reader) {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              // 处理缓冲区中剩余的数据
              if (buffer.trim()) {
                processEventData(buffer);
              }
              break;
            }

            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;

            // 查找完整的事件（兼容 \n\n 和 \r\n\r\n）
            const findEventDelimiter = () => {
              const lfIndex = buffer.indexOf('\n\n');
              const crlfIndex = buffer.indexOf('\r\n\r\n');
              if (lfIndex === -1 && crlfIndex === -1) {
                return { eventEnd: -1, delimiterLength: 0 };
              }
              if (lfIndex !== -1 && (crlfIndex === -1 || lfIndex < crlfIndex)) {
                return { eventEnd: lfIndex, delimiterLength: 2 };
              }
              return { eventEnd: crlfIndex, delimiterLength: 4 };
            };

            let { eventEnd, delimiterLength } = findEventDelimiter();
            while (eventEnd !== -1) {
              const eventData = buffer.substring(0, eventEnd);
              buffer = buffer.substring(eventEnd + delimiterLength);

              processEventData(eventData);

              ({ eventEnd, delimiterLength } = findEventDelimiter());
            }
          }

          // 处理事件数据的函数
          function processEventData(eventData: string) {
            if (!eventData) {
              return;
            }

            const normalized = eventData.replace(/\r/g, '');
            if (normalized.trim() === '') {
              return;
            }

            const lines = normalized.split('\n');
            let eventType = '';
            const dataLines: string[] = [];

            for (const line of lines) {
              if (line.startsWith('event:')) {
                eventType = line.substring(6).trim();
              } else if (line.startsWith('data:')) {
                dataLines.push(line.substring(5)); // 保留空格
              }
            }

            const data = dataLines.join('\n');

            // 根据事件类型处理
            switch (eventType) {
              case 'error':
                message.error('AI响应错误: ' + data);
                setAiLoading(false);
                break;
              case 'done':
                setAiLoading(false);
                break;
              case 'message':
                if (data !== undefined && data !== null) {
                  // 处理换行符占位符
                  const processedData = data.replace(/<\|newline\|>/g, '\n');
                  streamedContent += processedData;
                  if (aiMessageId) {
                    enqueueTypewriter(aiMessageId, processedData);
                  }
                }
                break;
              default:
                // 如果有数据但没有明确的事件类型，也当作消息处理
                if (data !== undefined && data !== null && data.trim()) {
                  const processedData = data.replace(/<\|newline\|>/g, '\n');
                  streamedContent += processedData;
                  if (aiMessageId) {
                    enqueueTypewriter(aiMessageId, processedData);
                  }
                }
            }
          }
        } finally {
          reader.releaseLock();
        }
      }

      if (aiMessageId) {
        finalizeTypewriter(aiMessageId, streamedContent);
      }
      setAiLoading(false);
      // 更新AI使用情况缓存
      console.log('🤖 AI对话完成，开始更新缓存...');
      cacheManager.updateCache('aiUsage').catch(err => console.warn('更新AI使用情况缓存失败:', err));

    } catch (error: any) {
      setAiLoading(false);
      const errMsg = (error?.response?.data?.message) || error.message || 'AI询问失败，请重试';

      // 针对常见情况给出更明确提示
      if (/次数限制|AI对话次数限制/.test(errMsg)) {
        message.error(errMsg + '，可升级VIP以解除限制');
      } else if (/未登录|过期/.test(errMsg)) {
        message.error(errMsg);
      } else if (/权限不足/.test(errMsg)) {
        message.error(errMsg);
      } else {
        message.error(errMsg);
      }

      // 不直接删除用户消息；只删除空的 AI 占位消息
      if (aiMessageId) {
        clearTypewriterState(aiMessageId);
      }
      setChatMessages(prev => prev.filter(m => !(m.type === 'ai' && !m.content)));
    }
  };

  // 处理文本选择
  const handleTextSelect = () => {
    const selection = window.getSelection();
    const text = selection?.toString().trim() || '';
    setSelectedText(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.ctrlKey && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
    if (e.ctrlKey && e.key === 'p') {
      e.preventDefault();
      togglePreview();
    }
  };

  return (
    <div className="editor-container" onKeyDown={handleKeyDown}>
      <Card>
        {/* 工具栏 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 16,
          padding: '0 0 16px 0',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <Title level={4} style={{ margin: 0 }}>
              {editorSource === 'new' ? '新建文档' : '编辑文档'}
            </Title>
            {hasUnsavedChanges && (
              <span style={{ color: '#faad14', fontSize: '12px' }}>
                • 未保存
              </span>
            )}
          </div>
          <Space>
            <Button
              icon={<Wand2 size={16} />}
              onClick={handleOneClickFormat}
              loading={isFormatting}
              disabled={!content.trim()}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                color: 'white'
              }}
              title="一键排版"
            >
              {isFormatting ? '排版中...' : '一键排版'}
            </Button>
            <Button
              icon={<MessageSquare size={16} />}
              onClick={handleAskAI}
              loading={aiLoading}
              style={{
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                border: 'none',
                color: 'white'
              }}
              title="问问小酷"
            >
              问问小酷
            </Button>
            <Button
              icon={isPreview ? <Edit size={16} /> : <Eye size={16} />}
              onClick={togglePreview}
              title={`切换到${isPreview ? '编辑' : '预览'}模式 (Ctrl+P)`}
            >
              {isPreview ? '编辑' : '预览'}
            </Button>
            <Button
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSave}
              disabled={!hasUnsavedChanges}
              title="保存文档 (Ctrl+S)"
            >
              保存
            </Button>
          </Space>
        </div>

        {/* 文档信息 */}
        <div style={{ marginBottom: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                标题
              </label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="请输入文档标题"
                size="large"
                disabled={isPreview}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 4, fontWeight: 500 }}>
                标签
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {tagName && (
                  <Tag
                    color={availableTags.find(t => t.id === tagId)?.color || '#1890ff'}
                    closable
                    onClose={handleRemoveTag}
                  >
                    {tagName}
                  </Tag>
                )}
                <Select
                  placeholder="选择标签"
                  style={{ width: 200 }}
                  onChange={handleSelectTag}
                  value={tagId || undefined}
                  allowClear
                  onClear={handleRemoveTag}
                  disabled={isPreview}
                  showSearch
                  filterOption={(input, option) => {
                    const text = (option?.label ?? option?.value ?? '') as string;
                    return text.toLowerCase().includes(input.toLowerCase());
                  }}
                  dropdownRender={(menu) => (
                    <>
                      {menu}
                      <div style={{ padding: '8px', borderTop: '1px solid #e8e8e8' }}>
                        <div style={{ marginBottom: 6 }}>
                          <Typography.Text type="secondary" style={{ fontSize: '11px' }}>添加新标签</Typography.Text>
                        </div>
                        <div style={{ marginBottom: 6 }}>
                          <Input
                            value={inputTag}
                            onChange={e => setInputTag(e.target.value)}
                            onPressEnter={handleAddTag}
                            placeholder="输入标签名称"
                            style={{ width: '100%' }}
                            maxLength={20}
                            size="small"
                          />
                        </div>
                        <div style={{ marginBottom: 6 }}>
                          <ColorPicker
                            value={newTagColor}
                            onChange={(color) => setNewTagColor(color.toHexString())}
                            size="small"
                            presets={[
                              {
                                label: '推荐',
                                colors: [
                                  '#1890ff', '#52c41a', '#faad14', '#f5222d',
                                  '#722ed1', '#13c2c2', '#eb2f96', '#fa541c'
                                ]
                              }
                            ]}
                          />
                        </div>
                        <Button
                          type="primary"
                          onClick={handleAddTag}
                          loading={addingTag}
                          disabled={!inputTag.trim()}
                          block
                          size="small"
                        >
                          添加标签
                        </Button>
                      </div>
                    </>
                  )}
                >
                  {availableTags.map(tag => (
                    <Option key={tag.id} value={tag.id}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div
                          style={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: tag.color,
                            marginRight: 6
                          }}
                        />
                        {tag.name}
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>
            </div>
          </Space>
        </div>

        {/* 编辑器/预览区域 */}
        <div style={{ minHeight: 'calc(100vh - 400px)' }}>
          {isPreview ? (
            <div
              style={{
                padding: '16px',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                backgroundColor: settings.darkMode ? '#1f1f1f' : '#fff',
                minHeight: 'calc(100vh - 400px)',
                overflow: 'auto'
              }}
            >
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ children }) => <h1 style={{ borderBottom: '2px solid #eee', paddingBottom: '8px' }}>{children}</h1>,
                  h2: ({ children }) => <h2 style={{ borderBottom: '1px solid #eee', paddingBottom: '4px' }}>{children}</h2>,
                  code: ({ inline, className, children, ...rest }: any) => (
                    <CodeBlock inline={inline} className={className} settings={settings} {...rest}>
                      {children}
                    </CodeBlock>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote style={{
                      borderLeft: '4px solid #dfe2e5',
                      paddingLeft: '16px',
                      margin: '16px 0',
                      color: '#6a737d'
                    }}>
                      {children}
                    </blockquote>
                  ),
                  table: ({ children }) => (
                    <table style={{
                      borderCollapse: 'collapse',
                      width: '100%',
                      margin: '16px 0'
                    }}>
                      {children}
                    </table>
                  ),
                  th: ({ children }) => (
                    <th style={{
                      border: '1px solid #dfe2e5',
                      padding: '8px 12px',
                      background: settings.darkMode ? '#2d2d2d' : '#f6f8fa',
                      fontWeight: 600,
                      textAlign: 'left'
                    }}>
                      {children}
                    </th>
                  ),
                  td: ({ children }) => (
                    <td style={{
                      border: '1px solid #dfe2e5',
                      padding: '8px 12px'
                    }}>
                      {children}
                    </td>
                  )
                }}
              >
                {content || '*预览区域为空，请在编辑模式下输入内容*'}
              </ReactMarkdown>
            </div>
          ) : (
            <TextArea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onSelect={handleTextSelect}
              placeholder="开始写作..."
              style={{
                minHeight: 'calc(100vh - 400px)',
                resize: 'none',
                fontSize: '14px',
                lineHeight: '1.6'
              }}
              autoSize={false}
            />
          )}
        </div>

        {/* 底部提示 */}
        <div style={{
          marginTop: 16,
          padding: '8px 0',
          borderTop: '1px solid #f0f0f0',
          fontSize: '12px',
          color: '#8c8c8c'
        }}>
          <Space split={<span>•</span>}>
            <span>Ctrl+S 保存</span>
            <span>Ctrl+P 切换预览</span>
            <span>支持 Markdown 语法</span>
          </Space>
        </div>
      </Card>

      {/* 一键排版确认Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Wand2 size={16} />
            <span>一键排版</span>
            {isFormatting && (
              <>
                <Spin size="small" />
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>正在排版中...</Typography.Text>
              </>
            )}
          </div>
        }
        open={showFormatConfirm}
        onCancel={() => setShowFormatConfirm(false)}
        width={800}
        footer={[
          <Button key="cancel" onClick={() => setShowFormatConfirm(false)}>
            取消
          </Button>,
          <Button key="apply" type="primary" onClick={applyFormatting} disabled={isFormatting || !formattedContent}>
            应用排版
          </Button>
        ]}
      >
        <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
          <Typography.Title level={5}>排版前：</Typography.Title>
          <div style={{
            background: '#f5f5f5',
            padding: '12px',
            borderRadius: '4px',
            marginBottom: '16px',
            maxHeight: '200px',
            overflow: 'auto'
          }}>
            <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '12px' }}>
              {originalContent}
            </pre>
          </div>

          <Typography.Title level={5}>排版后：</Typography.Title>
          <div style={{
            background: '#f0f9ff',
            padding: '12px',
            borderRadius: '4px',
            border: '1px solid #e1f5fe',
            maxHeight: '200px',
            overflow: 'auto'
          }}>
            {isFormatting ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin />
                <div style={{ marginTop: '8px', color: '#666' }}>正在排版中...</div>
              </div>
            ) : (
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '12px' }}>
                {formattedContent || '等待排版结果...'}
              </pre>
            )}
          </div>
        </div>
      </Modal>

      {/* 问问小酷聊天Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <MessageSquare size={16} />
            <span>问问小酷</span>
          </div>
        }
        open={aiChatVisible}
        onCancel={() => {
          resetAiChatState();
        }}
        width={700}
        height={600}
        footer={null}
        styles={{
          body: { padding: 0, height: '500px' }
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: '500px',
          border: '1px solid #e8e8e8',
          borderRadius: '6px'
        }}>
          {/* 聊天消息区域 */}
          <div
            ref={chatContainerRef}
            style={{
              flex: 1,
              padding: '16px',
              overflowY: 'auto',
              backgroundColor: '#fafafa'
            }}
          >
            {chatMessages.length === 0 ? (
              <div style={{
                textAlign: 'center',
                color: '#999',
                marginTop: '50px',
                fontSize: '14px'
              }}>
                <MessageSquare size={48} style={{ marginBottom: '16px', opacity: 0.3 }} />
                <div>开始与小酷对话吧！</div>
                <div style={{ fontSize: '12px', marginTop: '8px' }}>
                  小酷可以帮您解答问题、整理思路、提供建议
                </div>
              </div>
            ) : (
              <div>
                {chatMessages.map((msg, index) => {
                  const isUser = msg.type === 'user';
                  const isLatestMessage = chatMessages[chatMessages.length - 1]?.id === msg.id;
                  const showThinking = msg.type === 'ai' && aiLoading && isLatestMessage;
                  const hasContent = !!msg.content?.trim();

                  return (
                    <div key={msg.id} style={{
                      marginBottom: index === chatMessages.length - 1 ? 0 : 16,
                      display: 'flex',
                      justifyContent: isUser ? 'flex-end' : 'flex-start'
                    }}>
                      <div style={{
                        maxWidth: '70%',
                        padding: '12px 16px',
                        borderRadius: '12px',
                        backgroundColor: isUser ? '#1890ff' : '#fff',
                        color: isUser ? '#fff' : '#333',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: hasContent && showThinking ? 12 : 4
                      }}>
                        <div style={{ whiteSpace: 'pre-wrap' }}>
                          {showThinking && !hasContent && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Spin size="small" />
                              <span>小酷正在思考中...</span>
                            </div>
                          )}
                          {hasContent && (
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={chatMarkdownComponents}
                            >
                              {msg.content}
                            </ReactMarkdown>
                          )}
                          {showThinking && hasContent && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: 4 }}>
                              <Spin size="small" />
                              <span>生成中...</span>
                            </div>
                          )}
                        </div>
                        <div style={{
                          fontSize: '11px',
                          opacity: 0.7,
                          textAlign: 'right'
                        }}>
                          {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 输入区域 */}
          <div style={{
            padding: '16px',
            borderTop: '1px solid #e8e8e8',
            backgroundColor: '#fff'
          }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <Input.TextArea
                value={currentInput}
                onChange={(e) => setCurrentInput(e.target.value)}
                placeholder="输入您的问题..."
                autoSize={{ minRows: 1, maxRows: 4 }}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    sendMessageToAI(currentInput);
                  }
                }}
                style={{ flex: 1 }}
              />
              <Button
                type="primary"
                onClick={() => sendMessageToAI(currentInput)}
                loading={aiLoading}
                disabled={!currentInput.trim()}
                style={{ height: '32px' }}
              >
                发送
              </Button>
            </div>
            <div style={{
              fontSize: '11px',
              color: '#999',
              marginTop: '8px',
              textAlign: 'center'
            }}>
              按 Enter 发送，Shift + Enter 换行
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EditorNew;
